import { useMutation } from '@tanstack/react-query';
import { ImageOff, Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  deleteCustomerChatImages,
  deleteSessionChatImages,
  isCustomerNumber,
} from '../../services/api/sessions';
import { getApiErrorDetails } from '../../utils/api-error';
import { cn } from '../../utils/cn';

interface Props {
  sessionId: string;
}

// Kundens begäran om radering (GDPR art. 17, #399). Två steg, eftersom raderingen
// inte går att ångra och länkarna i redan skickade supportmejl slutar fungera.
export const EraseChatImagesButton = ({ sessionId }: Props) => {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [result, setResult] = useState<{ isError: boolean; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (target: { sessionId: string } | { customerId: string }) =>
      'sessionId' in target
        ? deleteSessionChatImages(target.sessionId)
        : deleteCustomerChatImages(target.customerId),
    onSuccess: ({ deleted }) => {
      setResult({
        isError: false,
        message: deleted === 0 ? 'Det fanns inga bilder att radera.' : `${deleted} bilder raderade.`,
      });
      setOpen(false);
      setCustomerId('');
    },
    onError: (error: unknown) => {
      setResult({ isError: true, message: `Kunde inte radera — ${getApiErrorDetails(error)}` });
    },
  });

  return (
    <div className="space-y-2">
      {!open ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setResult(null);
          }}
          className="inline-flex items-center gap-1.5 text-sm text-red-700 hover:text-red-900 hover:underline transition"
        >
          <ImageOff className="h-4 w-4" /> Radera kundens chattbilder
        </button>
      ) : (
        <div className="space-y-2 rounded-lg border border-red-200 bg-red-50/50 p-3">
          <p className="text-xs text-slate-600">
            Raderar bilderna och deras rader. Länkar i redan skickade supportmejl slutar fungera.
            Går inte att ångra.
          </p>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ sessionId })}
            className={cn(
              'flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition',
              'hover:bg-red-500 disabled:bg-slate-300',
            )}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageOff className="h-4 w-4" />}
            Radera bilderna i den här sessionen
          </button>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="Kundnummer"
              disabled={mutation.isPending}
              className="w-40 rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 disabled:bg-slate-100"
            />
            <button
              type="button"
              disabled={mutation.isPending || !isCustomerNumber(customerId)}
              onClick={() => mutation.mutate({ customerId: customerId.trim() })}
              className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:border-slate-200 disabled:text-slate-400"
            >
              Radera alla kundens bilder
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Kundnumret når bara bilder sparade från 2026-10-23, när kunden var verifierad.
          </p>
          {!mutation.isPending && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Avbryt
            </button>
          )}
        </div>
      )}
      {result && (
        <p className={cn('text-sm', result.isError ? 'text-red-700' : 'text-green-800')}>{result.message}</p>
      )}
    </div>
  );
};
